import { CarritoRepository } from '../../domain/models/carrito/gateway/invoice-repository';
import { Observable, of } from 'rxjs';
import { Product } from '../../domain/models/products/products';
import { Carrito } from '../../domain/models/carrito/carrito';

interface CartItem {
  sku: string;
  auxsku: string;
  name: string;
  amount: number;
  total: number;
  value: number;
  buy_value?: number;
  sell_tax?: Tax;
  buy_tax?: Tax;
  [key: string]: any; // Kept for dynamic field access like item[taxField]
}

interface Tax {
  id: string;
  name: string;
  value: number;
}

interface TaxGroup {
  id: string;
  metadata: Tax;
  value: number;
  subtotal: number;
}

export default class CarritoRepositoryImp extends CarritoRepository {
  private static instance: CarritoRepositoryImp;
  private items: CartItem[] = [];
  private taxes: TaxGroup[] = [];
  private total = 0;
  private type: 'sell' | 'buy' | '' = "";
  private aux: Record<'sell' | 'buy', string> = {
    "sell": "sell_tax",
    "buy": "buy_tax",
  }
  private aux2 = {
    "tax": {
      "sell": "sell_tax",
      "buy": "buy_tax",
    },
    "value": {
      "sell": "value",
      "buy": "buy_value",
    }
  }

  private constructor() {
    super();
  }

  public static getInstance(): CarritoRepositoryImp {
    if (!CarritoRepositoryImp.instance) {
      CarritoRepositoryImp.instance = new CarritoRepositoryImp();
    }
    return CarritoRepositoryImp.instance;
  }

  // --- Interface Methods (Returning Observables) ---

  add(item: CartItem): Observable<Product> {
    this.addSync(item);
    return of(item as unknown as Product);
  }

  setType(type: 'sell' | 'buy'): Observable<void> {
    this.setTypeSync(type);
    return of(undefined);
  }

  remove(sku: string): Observable<Product> {
    const itemToRemove = this.items.find(item => item.sku === sku);
    this.removeSync(sku);
    return of(itemToRemove as unknown as Product);
  }

  get(): Observable<Carrito> {
    return of(this.items as unknown as Carrito);
  }

  getTaxes(): Observable<TaxGroup[]> {
    this.getTaxesSync();
    return of(this.taxes);
  }

  clear(): Observable<Carrito> {
    this.clearSync();
    return of([] as unknown as Carrito);
  }

  update(auxsku: string, key: string, value: any): Observable<Product> {
    this.updateSync(auxsku, key, value);
    const updatedItem = this.items.find(item => item.auxsku === auxsku);
    return of(updatedItem as unknown as Product);
  }

  getTotal(): Observable<number> {
    this.getTotalSync();
    return of(this.total);
  }

  // --- Synchronous Methods for Context ---

  addSync(item: CartItem): void {
    this.items.push(item);
  }

  setTypeSync(type: 'sell' | 'buy'): void {
    this.type = type
  }

  getTypeSync(): 'sell' | 'buy' | '' {
    return this.type
  }

  getTypesSync() {
    return this.aux2
  }

  removeSync(auxsku: string): void {
    this.items = this.items.filter(item => item.auxsku !== auxsku);
  }

  getSync(): CartItem[] {
    return this.items;
  }

  getTaxesSync(): TaxGroup[] {
    this.taxes = []
    if (!this.type) return [];

    this.items.forEach((item) => {
      let taxField = this.aux[this.type as 'sell' | 'buy'];
      if (!item[taxField]) {
        taxField = this.aux['sell'];
        if (!item[taxField]) {
          return;
        }
      }
      const itemTax = item[taxField] as Tax;
      const tax = this.taxes.find(t => t.id === itemTax.id);
      const precio_final = item.total

      // Priorizar el impuesto pre-calculado (auxtax) para respetar valores de plantillas
      const taxAmount = (item.auxtax !== undefined && item.auxtax !== null)
        ? Math.round(item.auxtax)
        : Math.round((precio_final * itemTax.value) / (1 + (itemTax.value || 0)));

      if (tax) {
        tax.subtotal += taxAmount;
      } else {
        this.taxes.push({
          "id": itemTax.id,
          "metadata": itemTax,
          "value": itemTax.value,
          "subtotal": taxAmount
        });
      }
    });

    return this.taxes;
  }

  clearSync(): void {
    this.items = [];
    this.taxes = [];
  }

  updateSync(auxsku: string, key: string, value: any): void {
    this.items.forEach((item: CartItem) => {
      if (item.auxsku === auxsku) {
        item[key] = value;
        const valueKey = this.aux2.value[this.type as 'sell' | 'buy'];
        item.total = Math.round(parseFloat(item[valueKey]) * parseFloat(item.amount.toString()));

        const taxField = this.aux[this.type as 'sell' | 'buy'];
        if (item[taxField]) {
          const itemTax = item[taxField] as Tax;
          item.auxtax = Math.round(item.total - (item.total / (1 + parseFloat(itemTax.value.toString() || '0'))));
        }
      }
    });
  }

  getTotalSync(): number {
    this.total = 0;
    if (!this.type) return 0;

    this.items.forEach((item) => {
      if (item.total !== undefined && item.total !== null) {
        this.total += Math.round(item.total);
      } else {
        const valueKey = this.aux2.value[this.type as 'sell' | 'buy'];
        this.total += Math.round(parseFloat(item[valueKey]) * parseFloat(item.amount.toString()));
      }
    });
    return this.total
  }
}
